import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Search } from 'lucide-react';

import { PageTransition } from '../components/PageTransition';
import PostRow from '../components/PostRow';
import Seo from '../components/Seo';
import { requestPosts } from '../actions/post';
import { filterPosts, sortPostsByDate } from '../utils/postHelpers';

function IndexSkeleton() {
  return (
    <div aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="grid gap-3 border-b border-rule py-7 sm:grid-cols-[8.5rem_1fr] sm:gap-8">
          <div className="h-3 w-24 animate-pulse rounded-sm bg-muted" />
          <div className="space-y-3">
            <div className="h-7 w-2/3 animate-pulse rounded-sm bg-muted" />
            <div className="h-4 w-full max-w-prose animate-pulse rounded-sm bg-muted" />
            <div className="h-4 w-1/2 animate-pulse rounded-sm bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Posts({ posts, fetchPosts }) {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(posts.length === 0);

  useEffect(() => {
    let active = true;
    Promise.resolve(fetchPosts()).finally(() => {
      if (active) setIsLoading(false);
    });
    return () => { active = false; };
  }, [fetchPosts]);

  const sorted = useMemo(() => sortPostsByDate(posts), [posts]);
  const results = useMemo(() => filterPosts(sorted, query), [sorted, query]);

  const postsSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Writing',
    url: 'https://dennisnyabuti.com/posts',
    description: 'Notes on software engineering, USSD, offline-first systems and AI integration.',
  };

  return (
    <>
      <Seo
        title="Writing | Dennis M. Nyabuti"
        description="Notes on software engineering, USSD, offline-first systems, cloud architecture and AI integration."
        path="/posts"
        keywords="software engineering blog, USSD, offline-first, cloud architecture, React, AI engineering"
        schema={postsSchema}
      />

      <PageTransition>
        <div className="shell pb-8 pt-12 sm:pt-16">
          <header className="border-b border-rule pb-10">
            <p className="label">Index</p>
            <h1 className="mt-5 text-title">Writing</h1>
            <p className="mt-5 max-w-prose text-deck text-muted-foreground">
              Notes from building software for constrained networks, small screens and
              places the cloud does not reach.
            </p>

            <div className="relative mt-10 max-w-md">
              <Search
                className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search posts"
                aria-label="Search posts"
                className="w-full border-0 border-b border-rule bg-transparent py-2.5 pl-7 pr-3 text-sm placeholder:text-muted-foreground focus:border-signal focus:outline-none focus:ring-0 transition-colors"
              />
            </div>

            <p className="label mt-4 h-4" aria-live="polite">
              {query
                ? `${results.length} ${results.length === 1 ? 'result' : 'results'}`
                : !isLoading && `${sorted.length} ${sorted.length === 1 ? 'post' : 'posts'}`}
            </p>
          </header>

          <div className="mt-2">
            {isLoading && sorted.length === 0 && <IndexSkeleton />}

            {!isLoading && sorted.length === 0 && (
              <p className="py-16 text-muted-foreground">
                Nothing published yet. The first post is being written.
              </p>
            )}

            {results.map((post) => (
              <PostRow key={post.ID || post.id} post={post} />
            ))}

            {sorted.length > 0 && results.length === 0 && query && (
              <p className="py-16 text-muted-foreground">
                No post matches &ldquo;{query}&rdquo;. Try a shorter search.
              </p>
            )}
          </div>
        </div>
      </PageTransition>
    </>
  );
}

Posts.propTypes = {
  posts: PropTypes.array.isRequired,
  fetchPosts: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({ posts: state.posts }),
  (dispatch) => ({ fetchPosts: () => dispatch(requestPosts()) }),
)(Posts);
